/**
 * Water VFX — splash bursts + a foam WAKE, pooled. Every element is anchored in
 * PLANET-LOCAL space and re-derived from the planet's live pose each frame, so the
 * foam STICKS to the spinning water surface (a fixed system-space point would drift
 * off as the planet rotates — "floating into the sky"). The wake is small, soft,
 * FILLED foam blobs (not rings) laid down densely behind a moving swimmer; the body
 * swims on and leaves them behind, so the overlapping trail reads as a wake.
 *
 * Derived locally from positions + the deterministic sea level — no networking.
 */
import * as THREE from "three";
import { createSurfaceColumn, surfaceColumn, seaSurface } from '@hyperlight/shared';
import type { Planet } from "../world/planet";

const contact = createSurfaceColumn(), contactDir = new THREE.Vector3();
function acceptsWaterFx(planet: Planet, point: THREE.Vector3): boolean {
  // Preserve existing water FX cost/placement. Explicit special basins may
  // still have freshwater rivers above them, so inspect the actual column.
  if (seaSurface(planet.def) === 'water' && planet.def.archetype!=='volcanic') return true;
  contactDir.copy(point).sub(planet.pose.center).applyQuaternion(planet.pose.invQuat).normalize();
  surfaceColumn(planet.sampler, planet.def, contactDir.x, contactDir.y, contactDir.z, contact);
  return contact.liquid === 'water';
}

const SPLASH_POOL = 18;
const FOAM_POOL = 80;
const RIPPLE_POOL = 44; // rain dimples — many, short-lived, on the water surface
const SPLASH_LIFE = 0.45;
const FOAM_LIFE = 1.0;
const RIPPLE_LIFE = 0.85;

/** A thin bright RING (transparent core) for an expanding rain ripple. */
function ringTex(): THREE.CanvasTexture {
  const s = 64;
  const cv = document.createElement("canvas");
  cv.width = cv.height = s;
  const ctx = cv.getContext("2d")!;
  const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  g.addColorStop(0, "rgba(255,255,255,0)");
  g.addColorStop(0.55, "rgba(235,248,255,0)");
  g.addColorStop(0.8, "rgba(240,250,255,0.85)");
  g.addColorStop(1, "rgba(220,240,255,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, s, s);
  return new THREE.CanvasTexture(cv);
}

function softBlob(centerA: number): THREE.CanvasTexture {
  const s = 64;
  const cv = document.createElement("canvas");
  cv.width = cv.height = s;
  const ctx = cv.getContext("2d")!;
  const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  g.addColorStop(0, `rgba(255,255,255,${centerA})`);
  g.addColorStop(0.5, "rgba(226,245,255,0.45)");
  g.addColorStop(1, "rgba(210,238,255,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, s, s);
  return new THREE.CanvasTexture(cv);
}

interface Anchor {
  planet: Planet | null;
  local: THREE.Vector3; // planet-LOCAL position (relative to centre, despun)
  t: number;
  base: number;
}

export class WaterFx {
  private splashes: (Anchor & { sprite: THREE.Sprite })[] = [];
  private foams: (Anchor & { mesh: THREE.Mesh })[] = [];
  private ripples: (Anchor & { mesh: THREE.Mesh })[] = [];
  private nextSplash = 0;
  private nextFoam = 0;
  private nextRipple = 0;
  private readonly planeN = new THREE.Vector3(0, 0, 1); // PlaneGeometry normal
  private readonly _w = new THREE.Vector3();
  private readonly _u = new THREE.Vector3();

  constructor(private scene: THREE.Scene) {
    const splashTex = softBlob(1);
    for (let i = 0; i < SPLASH_POOL; i++) {
      const mat = new THREE.SpriteMaterial({ map: splashTex, transparent: true, depthWrite: false, opacity: 0 });
      const sp = new THREE.Sprite(mat);
      sp.visible = false;
      sp.renderOrder = 5;
      scene.add(sp);
      this.splashes.push({ sprite: sp, planet: null, local: new THREE.Vector3(), t: 0, base: 1 });
    }
    const foamTex = softBlob(0.85);
    const geo = new THREE.PlaneGeometry(1, 1);
    for (let i = 0; i < FOAM_POOL; i++) {
      const mat = new THREE.MeshBasicMaterial({ map: foamTex, transparent: true, depthWrite: false, opacity: 0, side: THREE.DoubleSide });
      const m = new THREE.Mesh(geo, mat);
      m.visible = false;
      m.renderOrder = 5;
      m.frustumCulled = false;
      scene.add(m);
      this.foams.push({ mesh: m, planet: null, local: new THREE.Vector3(), t: 0, base: 1 });
    }
    const ringT = ringTex();
    for (let i = 0; i < RIPPLE_POOL; i++) {
      const mat = new THREE.MeshBasicMaterial({ map: ringT, transparent: true, depthWrite: false, opacity: 0, side: THREE.DoubleSide });
      const m = new THREE.Mesh(geo, mat);
      m.visible = false;
      m.renderOrder = 5;
      m.frustumCulled = false;
      scene.add(m);
      this.ripples.push({ mesh: m, planet: null, local: new THREE.Vector3(), t: 0, base: 1 });
    }
  }

  private localOf(planet: Planet, sysPos: THREE.Vector3, out: THREE.Vector3): void {
    out.copy(sysPos).sub(planet.pose.center).applyQuaternion(planet.pose.invQuat);
  }

  /** A splash burst + a foam blob (water entry, projectile impacts). */
  splash(planet: Planet, sysPos: THREE.Vector3, scale = 1): void {
    if (!acceptsWaterFx(planet, sysPos)) return;
    const sp = this.splashes[this.nextSplash];
    this.nextSplash = (this.nextSplash + 1) % SPLASH_POOL;
    sp.planet = planet;
    this.localOf(planet, sysPos, sp.local);
    sp.t = SPLASH_LIFE;
    sp.base = scale;
    sp.sprite.visible = true;
    this.foam(planet, sysPos, scale * 1.1);
  }

  /** An expanding ring on the water — a single raindrop dimpling the surface. */
  ripple(planet: Planet, sysPos: THREE.Vector3, scale = 1): void {
    if (!acceptsWaterFx(planet, sysPos)) return;
    const r = this.ripples[this.nextRipple];
    this.nextRipple = (this.nextRipple + 1) % RIPPLE_POOL;
    r.planet = planet;
    this.localOf(planet, sysPos, r.local);
    r.t = RIPPLE_LIFE;
    r.base = scale;
    r.mesh.visible = true;
  }

  /** A foam blob on the surface — laid down behind a swimmer to build the wake trail. */
  foam(planet: Planet, sysPos: THREE.Vector3, scale = 1): void {
    if (!acceptsWaterFx(planet, sysPos)) return;
    const f = this.foams[this.nextFoam];
    this.nextFoam = (this.nextFoam + 1) % FOAM_POOL;
    f.planet = planet;
    this.localOf(planet, sysPos, f.local);
    f.t = FOAM_LIFE;
    f.base = scale;
    f.mesh.visible = true;
  }

  // Anchor every decal to the planet's RENDERED group transform (interpolated between sim
  // ticks), NOT the raw sim pose — otherwise the decal sits at the sim pose while the water
  // surface renders at the lerped pose, and the few-cm mismatch jitters each frame.
  update(dt: number): void {
    for (const sp of this.splashes) {
      if (sp.t <= 0) continue;
      sp.t -= dt;
      const mat = sp.sprite.material as THREE.SpriteMaterial;
      if (sp.t <= 0 || !sp.planet) { sp.sprite.visible = false; mat.opacity = 0; continue; }
      const k = 1 - sp.t / SPLASH_LIFE;
      this._w.copy(sp.local).applyQuaternion(sp.planet.group.quaternion).add(sp.planet.group.position);
      sp.sprite.position.copy(this._w);
      sp.sprite.scale.setScalar(sp.base * (1.0 + k * 2.4));
      mat.opacity = (1 - k) * 0.85;
    }
    for (const f of this.foams) {
      if (f.t <= 0) continue;
      f.t -= dt;
      const mat = f.mesh.material as THREE.MeshBasicMaterial;
      if (f.t <= 0 || !f.planet) { f.mesh.visible = false; mat.opacity = 0; continue; }
      const k = 1 - f.t / FOAM_LIFE;
      // Live position from the planet's RENDERED group → the foam rides the spin smoothly.
      this._w.copy(f.local).applyQuaternion(f.planet.group.quaternion); // offset from centre (render)
      this._u.copy(this._w).normalize(); // surface normal there
      this._w.add(f.planet.group.position);
      f.mesh.position.copy(this._w);
      f.mesh.quaternion.setFromUnitVectors(this.planeN, this._u);
      const s = f.base * (1.1 + k * 1.0); // small, barely spreads (foam, not an expanding ring)
      f.mesh.scale.set(s, s, 1);
      mat.opacity = (1 - k) * 0.5;
    }
    for (const r of this.ripples) {
      if (r.t <= 0) continue;
      r.t -= dt;
      const mat = r.mesh.material as THREE.MeshBasicMaterial;
      if (r.t <= 0 || !r.planet) { r.mesh.visible = false; mat.opacity = 0; continue; }
      const k = 1 - r.t / RIPPLE_LIFE;
      this._w.copy(r.local).applyQuaternion(r.planet.group.quaternion);
      this._u.copy(this._w).normalize(); // surface normal there
      this._w.add(r.planet.group.position);
      r.mesh.position.copy(this._w);
      r.mesh.quaternion.setFromUnitVectors(this.planeN, this._u);
      const s = r.base * (0.3 + k * 2.6); // expands outward like a real ripple
      r.mesh.scale.set(s, s, 1);
      mat.opacity = (1 - k * k) * 0.6; // bright, then fades as it spreads
    }
  }

  dispose(): void {
    for (const sp of this.splashes) { this.scene.remove(sp.sprite); (sp.sprite.material as THREE.SpriteMaterial).dispose(); }
    for (const f of this.foams) { this.scene.remove(f.mesh); (f.mesh.material as THREE.MeshBasicMaterial).dispose(); }
    for (const r of this.ripples) { this.scene.remove(r.mesh); (r.mesh.material as THREE.MeshBasicMaterial).dispose(); }
  }
}
